import { Typography } from '@mui/material';
import classNames from 'classnames';
import moment from 'moment/moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { TournamentStatus } from '@okletsplay/core';
import { withTournamentStatus } from 'shared/HOC/withTournamentStatus';
import { useStyles } from './MatchGameTimer.styles';

interface MatchGameTimerProps {
  startedAt: string;
  maxGameDuration: number;
}

const INTERVAL = 1000;

export const MatchGameTimerComponent: FC<MatchGameTimerProps> = ({
  startedAt,
  maxGameDuration,
}) => {
  const { classes } = useStyles();

  const [currentTime, setCurrentTime] = useState(moment().utc());

  const isTimerExpired = useMemo(() => {
    const secondsDiff = moment(currentTime).diff(startedAt, 'seconds');
    return Math.abs(secondsDiff / 60) >= maxGameDuration;
  }, [currentTime, maxGameDuration, startedAt]);

  const formattedDate = useMemo(() => {
    const secondsDiff = moment(currentTime).diff(startedAt, 'seconds');
    const duration = moment.duration(secondsDiff, 'seconds');
    const minutes = Math.abs(duration.minutes());
    const seconds = Math.abs(duration.seconds());
    const hours = Math.abs(duration.hours());

    const milliseconds = moment.utc(duration.asMilliseconds());
    if (minutes === 0 && hours === 0 && seconds >= 0) {
      return milliseconds.format('ss[s]');
    }

    if (minutes < 60 && hours === 0) {
      return milliseconds.format('mm[m] ss[s]');
    }

    return milliseconds.format('hh[h] mm[m] ss[s]');
  }, [currentTime, startedAt]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(moment().utc()), INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [setCurrentTime]);

  if (!startedAt) {
    return null;
  }

  return (
    <Typography className={classNames(classes.root, { [classes.expired]: isTimerExpired })}>
      {`${isTimerExpired ? 'Overtime: ' : ''}${formattedDate}`}
    </Typography>
  );
};

export const MatchGameTimer = withTournamentStatus(MatchGameTimerComponent, [
  TournamentStatus.Live,
]);
